import React, { useState, useEffect } from 'react';
import { warehousesAPI, Warehouse } from '../../services/api';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  BuildingOfficeIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import WarehouseForm from './WarehouseForm';
import WarehouseDetail from './WarehouseDetail';
import { Card, CardBody, Button, Badge, Spinner, EmptyState, Modal } from '../ui';

const WarehouseManagement: React.FC = () => {
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [selectedWarehouse, setSelectedWarehouse] = useState<Warehouse | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  useEffect(() => {
    fetchWarehouses();
  }, []);

  const fetchWarehouses = async () => {
    try {
      setLoading(true);
      const data = await warehousesAPI.getAll();
      setWarehouses(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch warehouses');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedWarehouse) return;

    try {
      setError('');
      setSuccess('');
      await warehousesAPI.delete(selectedWarehouse.id);
      setSuccess('Warehouse deleted successfully!');
      setShowDeleteDialog(false);
      setSelectedWarehouse(null);
      fetchWarehouses();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete warehouse');
    }
  };

  const handleView = (warehouse: Warehouse) => {
    setSelectedWarehouse(warehouse);
    setShowDetail(true);
  };

  const handleEdit = (warehouse: Warehouse) => {
    setSelectedWarehouse(warehouse);
    setShowEditForm(true);
  };

  const handleDeleteClick = (warehouse: Warehouse) => {
    setSelectedWarehouse(warehouse);
    setShowDeleteDialog(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-neutral-900">Warehouses</h2>
          <p className="mt-1 text-sm text-neutral-600">
            Manage warehouse locations and their hierarchical structure
          </p>
        </div>
        <Button
          variant="primary"
          onClick={() => setShowCreateForm(true)}
          leftIcon={<PlusIcon className="h-5 w-5" />}
        >
          Add Warehouse
        </Button>
      </div>

      {/* Messages */}
      {error && (
        <div className="mb-4 bg-error-50 border border-error-200 text-error-600 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 bg-success-50 border border-success-200 text-success-600 px-4 py-3 rounded-lg">
          {success}
        </div>
      )}

      {/* Warehouses Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {warehouses.map((warehouse) => (
          <Card key={warehouse.id} variant="elevated" hoverable>
            <CardBody className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-center">
                  <BuildingOfficeIcon className="h-8 w-8 text-primary-600" />
                  <div className="ml-3">
                    <h3 className="text-lg font-semibold text-neutral-900">{warehouse.name}</h3>
                    <p className="text-sm text-neutral-500">Code: {warehouse.code}</p>
                  </div>
                </div>
                <Badge variant={warehouse.isActive ? 'success' : 'neutral'}>
                  {warehouse.isActive ? 'Active' : 'Inactive'}
                </Badge>
              </div>

              <div className="mt-4 space-y-2">
                {warehouse.address && (
                  <p className="text-sm text-neutral-600">
                    <span className="font-medium">Address:</span> {warehouse.address}
                  </p>
                )}
                {warehouse.city && (
                  <p className="text-sm text-neutral-600">
                    <span className="font-medium">City:</span> {warehouse.city}
                  </p>
                )}
                {warehouse.capacity && (
                  <p className="text-sm text-neutral-600">
                    <span className="font-medium">Capacity:</span> {warehouse.capacity} units
                  </p>
                )}
                <p className="text-sm text-neutral-600">
                  <span className="font-medium">Shelves:</span> {warehouse._count?.shelves || 0}
                </p>
              </div>

              <div className="mt-4 flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleView(warehouse)}
                  leftIcon={<EyeIcon className="h-4 w-4" />}
                  className="flex-1"
                >
                  View
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleEdit(warehouse)}
                  leftIcon={<PencilIcon className="h-4 w-4" />}
                  className="flex-1"
                >
                  Edit
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => handleDeleteClick(warehouse)}
                >
                  <TrashIcon className="h-4 w-4" />
                </Button>
              </div>
            </CardBody>
          </Card>
        ))}
      </div>

      {warehouses.length === 0 && (
        <EmptyState
          icon={<BuildingOfficeIcon className="h-12 w-12" />}
          title="No warehouses"
          description="Get started by creating a new warehouse."
          action={
            <Button
              variant="primary"
              onClick={() => setShowCreateForm(true)}
              leftIcon={<PlusIcon className="h-5 w-5" />}
            >
              Add Warehouse
            </Button>
          }
        />
      )}

      {/* Create Form Modal */}
      {showCreateForm && (
        <WarehouseForm
          onClose={() => setShowCreateForm(false)}
          onSuccess={() => {
            setShowCreateForm(false);
            fetchWarehouses();
            setSuccess('Warehouse created successfully!');
          }}
        />
      )}

      {/* Edit Form Modal */}
      {showEditForm && selectedWarehouse && (
        <WarehouseForm
          warehouse={selectedWarehouse}
          onClose={() => {
            setShowEditForm(false);
            setSelectedWarehouse(null);
          }}
          onSuccess={() => {
            setShowEditForm(false);
            setSelectedWarehouse(null);
            fetchWarehouses();
            setSuccess('Warehouse updated successfully!');
          }}
        />
      )}

      {/* Detail Modal */}
      {showDetail && selectedWarehouse && (
        <WarehouseDetail
          warehouseId={selectedWarehouse.id}
          onClose={() => {
            setShowDetail(false);
            setSelectedWarehouse(null);
          }}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Modal
        isOpen={showDeleteDialog && !!selectedWarehouse}
        onClose={() => {
          setShowDeleteDialog(false);
          setSelectedWarehouse(null);
        }}
        title="Delete Warehouse"
        size="md"
      >
        <p className="text-sm text-neutral-500 mb-6">
          Are you sure you want to delete "{selectedWarehouse?.name}"? This action cannot be undone
          and will also delete all associated shelves, bins, and trays.
        </p>
        <div className="flex justify-end space-x-3">
          <Button
            variant="outline"
            onClick={() => {
              setShowDeleteDialog(false);
              setSelectedWarehouse(null);
            }}
          >
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default WarehouseManagement;

